import {useMemo, useCallback} from 'react';
import {FetchComplianceResponse} from '../../lib/types';
import {parseFilterValues} from '../no-filters/utils/parse-filter-values';
import {SPECIALTY_COLOR_PALETTE, SPECIALTY_COLOR_FALLBACK, MAX_SPECIALTY_SELECTIONS} from '../../lib/specialty-colors';

export const MAX_SPECIALTY_SELECTION = MAX_SPECIALTY_SELECTIONS;

export interface SpecialtyScore {
    name: string;
    specialtyCode: string;
    compliance?: boolean;
}

interface UseSpecialtyFilterProps {
    complianceScores: FetchComplianceResponse;
    specialtyFilter: string;
    searchTerm: string;
}

export function useSpecialtyFilter({
    complianceScores,
    specialtyFilter,
    searchTerm,
}: UseSpecialtyFilterProps) {
    const selectedCodes = useMemo(() =>
        parseFilterValues(specialtyFilter),
    [specialtyFilter]
    );

    // Helper to check if specialty is selected
    const isSpecialtySelected = useCallback(
        (specialtyCode: string): boolean => selectedCodes.includes(specialtyCode),
        [selectedCodes]
    );

    // Filter specialties based on search term and sort selected to top in ascending order
    const filteredScores = useMemo(() => {
        let filtered = complianceScores;

        // Filter by search term if provided
        if (searchTerm.trim()) {
            filtered = complianceScores.filter((score: SpecialtyScore) =>
                score.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort: selected items first (alphabetically), then unselected items (alphabetically)
        filtered = [...filtered].sort((a: SpecialtyScore, b: SpecialtyScore) => {
            const aSelected = isSpecialtySelected(a.specialtyCode);
            const bSelected = isSpecialtySelected(b.specialtyCode);

            // Both selected or both unselected - sort alphabetically by name
            if (aSelected === bSelected) {
                return a.name.localeCompare(b.name);
            }

            // Selected items come first
            return aSelected ? -1 : 1;
        });

        return filtered;
    }, [complianceScores, searchTerm, isSpecialtySelected]);

    // Get color for a selected specialty based on its selection order
    const getSpecialtyColor = useCallback(
        (specialtyCode: string): string => {
            const index = selectedCodes.indexOf(specialtyCode);
            if (index === -1) return '';

            // Use color from palette if available, otherwise use fallback
            if (index < SPECIALTY_COLOR_PALETTE.length) {
                return SPECIALTY_COLOR_PALETTE[index];
            }
            return SPECIALTY_COLOR_FALLBACK;
        },
        [selectedCodes]
    );

    return {
        filteredScores,
        getSpecialtyColor,
        isSpecialtySelected,
    };
}
